/**
 * Safely parses a price string to a number, handling various formats and edge cases
 * @param priceString - The price string to parse (can contain currency symbols, spaces, commas)
 * @param currency - Optional currency code to help with parsing
 * @returns A valid number or 0 if parsing fails
 */
const safePriceParser = (priceString: string | null | undefined, currency?: string): number => {
  if (!priceString || typeof priceString !== 'string') {
    return 0;
  }

  // Remove currency symbols and whitespace
  let cleaned = priceString.trim().replace(/\s/g, '');

  // Handle MXN currency (starts with $)
  if (currency === 'MXN' && cleaned.startsWith('$')) {
    cleaned = cleaned.slice(1);
  }

  // Remove common currency symbols
  cleaned = cleaned.replace(/[PLN€$£¥]/g, '');

  // Replace comma with dot for decimal separator
  cleaned = cleaned.replace(',', '.');

  // Remove any remaining non-numeric characters except dot
  cleaned = cleaned.replace(/[^\d.]/g, '');

  // Parse the number
  const parsed = parseFloat(cleaned);

  // Validate the result
  if (isNaN(parsed) || !isFinite(parsed) || parsed < 0) {
    return 0;
  }
  return parsed;
};

/**
 * Safely extracts price from DOM element text content
 * @param selector - CSS selector for the price element
 * @param currency - Optional currency code
 * @returns A valid number or 0 if extraction fails
 */
export const safeExtractPriceFromDOM = (selector: string, currency?: string): number => {
  if (typeof window === 'undefined' || !document) {
    return 0;
  }
  try {
    const element = document.querySelector(selector);
    if (!element?.textContent) {
      return 0;
    }
    return safePriceParser(element.textContent, currency);
  } catch (error) {
    console.warn(`Failed to extract price from selector "${selector}":`, error);
    return 0;
  }
};

/**
 * Safely parses a price value, ensuring it's a valid number
 * @param price - Price value (can be number, string, or null/undefined)
 * @param currency - Optional currency code
 * @returns A valid number or 0 if parsing fails
 */
export const safeParsePrice = (price: number | string | null | undefined, currency?: string): number => {
  if (typeof price === 'number') {
    if (isNaN(price) || !isFinite(price) || price < 0) {
      return 0;
    }
    return price;
  }
  if (typeof price === 'string') {
    return safePriceParser(price, currency);
  }
  return 0;
};
export default safePriceParser;